import React, { useState } from "react";
import {
  Button,
  Box,
  Typography,
  InputAdornment,
  TextField,
  Alert,
  Modal,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import md5 from "blueimp-md5";

import PersonIcon from "@mui/icons-material/Person";
import HttpsIcon from "@mui/icons-material/Https";

import { motion } from "framer-motion";

// --- 🧠 MINI NEURAL COMPONENT (SPECIFIC FOR CIRCLES) ---
const MiniNeuralNetwork = () => {
  // Nodes optimized for a smaller circular view
  const nodes = [
    { x: 50, y: 50 },
    { x: 150, y: 80 },
    { x: 250, y: 40 },
    { x: 80, y: 150 },
    { x: 200, y: 180 },
    { x: 320, y: 150 },
    { x: 40, y: 250 },
    { x: 160, y: 300 },
    { x: 280, y: 260 },
    { x: 350, y: 320 },
    { x: 100, y: 350 },
    { x: 300, y: 50 },
  ];

  const connections = [
    [0, 1],
    [0, 3],
    [1, 2],
    [1, 4],
    [2, 5],
    [3, 4],
    [3, 6],
    [4, 5],
    [4, 7],
    [4, 8],
    [5, 8],
    [6, 7],
    [7, 8],
    [8, 9],
    [7, 10],
    [2, 11],
  ];

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 2,
        pointerEvents: "none",
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 400 400"
        preserveAspectRatio="xMidYMid slice"
        style={{ overflow: "visible" }}
      >
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* CONNECTIONS */}
        {connections.map(([start, end], index) => (
          <motion.line
            key={`line-${index}`}
            x1={nodes[start].x}
            y1={nodes[start].y}
            x2={nodes[end].x}
            y2={nodes[end].y}
            stroke="rgba(255, 255, 255, 0.4)"
            strokeWidth="1.5"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{
              pathLength: 1,
              opacity: [0.2, 0.6, 0.2],
            }}
            transition={{
              duration: 2 + Math.random(),
              repeat: Infinity,
              ease: "easeInOut",
              delay: index * 0.1,
            }}
          />
        ))}

        {/* MOVING SIGNALS */}
        {connections.map(([start, end], index) => (
          <motion.circle key={`signal-${index}`} r="3" fill="#ffffff" filter="url(#glow)">
            <animateMotion
              dur={`${1.5 + Math.random() * 2}s`}
              repeatCount="indefinite"
              path={`M${nodes[start].x},${nodes[start].y} L${nodes[end].x},${nodes[end].y}`}
            />
          </motion.circle>
        ))}

        {/* NODES */}
        {nodes.map((node, i) => (
          <motion.circle
            key={`node-${i}`}
            cx={node.x}
            cy={node.y}
            r="4"
            fill="#ffffff"
            filter="url(#glow)"
            initial={{ scale: 0 }}
            animate={{ scale: [1, 1.5, 1] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.2,
            }}
          />
        ))}
      </svg>
    </Box>
  );
};

// ----------------------------------------------------------

const FORGOT_PASSWORD_STEPS = {
  EMAIL_INPUT: "emailInput",
  OTP_INPUT: "otpInput",
  RESET_PASSWORD: "resetPassword",
  SUCCESS: "success",
};

const SignInForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    emailAddress: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [playSuccessAnim, setPlaySuccessAnim] = useState(false);

  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordCurrentStep, setForgotPasswordCurrentStep] = useState(
    FORGOT_PASSWORD_STEPS.EMAIL_INPUT
  );
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordOtp, setForgotPasswordOtp] = useState("");
  const [forgotPasswordNewPassword, setForgotPasswordNewPassword] = useState("");
  const [forgotPasswordConfirmPassword, setForgotPasswordConfirmPassword] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [forgotPasswordSuccessMessage, setForgotPasswordSuccessMessage] = useState("");

  const [emailError, setEmailError] = useState("");

  const validateEmail = (email) => {
    if (!email.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) return "Please enter a valid email address";
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "emailAddress") {
      setEmailError(validateEmail(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError("");

    const emailValidationError = validateEmail(formData.emailAddress);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }

    setLoading(true);

    try {
      const hashedPassword = md5(formData.password);
      const API_BASE_URL = process.env.REACT_APP_API_URL;

      const response = await fetch(`${API_BASE_URL}/PatientPortal/patientLogin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          emailAddress: formData.emailAddress,
          password: hashedPassword,
          pin: 0,
        }),
      });

      const raw = await response.text();
      let result = {};
      try {
        result = raw ? JSON.parse(raw) : {};
      } catch {
        result = { message: raw };
      }

      if (!response.ok || !result.token || !result.userName) {
        throw new Error(result.message || "Invalid credentials");
      }
      

      localStorage.setItem("neuro_patientportal_auth_token", result.token);
      localStorage.setItem("neuro_patientportal_userEmail", formData.emailAddress);
      localStorage.setItem( "neuro_patientportal_userName",formData.userName);




      setPlaySuccessAnim(true);
    } catch (err) {
      setError(err.message || "Login failed.");
      setLoading(false);
    }
  };

  const handleAnimationComplete = () => {
  const userName = localStorage.getItem("neuro_patientportal_userName");

  if (playSuccessAnim && userName && userName !== "undefined") {
    navigate("/dashboard/forms");
  }
};

  const forgotPasswordModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", sm: 400 },
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
    outline: "none",
  };

  const handleForgotPasswordClose = () => {
    setShowForgotPasswordModal(false);
    setForgotPasswordCurrentStep(FORGOT_PASSWORD_STEPS.EMAIL_INPUT);
    setForgotPasswordEmail("");
    setForgotPasswordOtp("");
    setForgotPasswordNewPassword("");
    setForgotPasswordConfirmPassword("");
    setForgotPasswordLoading(false);
    setForgotPasswordError("");
    setForgotPasswordSuccessMessage("");
  };

  // --- API INTEGRATIONS FOR FORGOT PASSWORD FLOW ---

  // 1) Send OTP: POST /PatientPortal/forgot-password?email=...
  const handleSendOtp = async () => {
    setForgotPasswordLoading(true);
    setForgotPasswordError("");
    setForgotPasswordSuccessMessage("");

    if (!forgotPasswordEmail) {
      setForgotPasswordError("Please enter your email address.");
      setForgotPasswordLoading(false);
      return;
    }
    const emailValidationError = validateEmail(forgotPasswordEmail);
    if (emailValidationError) {
      setForgotPasswordError(emailValidationError);
      setForgotPasswordLoading(false);
      return;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      const url = `${API_BASE_URL}/PatientPortal/forgot-password?email=${encodeURIComponent(
        forgotPasswordEmail
      )}`;

      const response = await fetch(url, {
        method: "POST",
      });

      const raw = await response.text();
      let res = {};
      try {
        res = raw ? JSON.parse(raw) : {};
      } catch {
        res = { message: raw };
      }

      if (!response.ok) {
        throw new Error(res.message || "Failed to send OTP.");
      }

      setForgotPasswordSuccessMessage(res.message || "OTP sent to your email.");
      setForgotPasswordCurrentStep(FORGOT_PASSWORD_STEPS.OTP_INPUT);
    } catch (err) {
      setForgotPasswordError(err.message || "Failed to send OTP.");
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // 2) Verify OTP: POST /PatientPortal/verify-otp  body: { email, otp }
  const handleVerifyOtp = async () => {
    setForgotPasswordLoading(true);
    setForgotPasswordError("");
    setForgotPasswordSuccessMessage("");

    if (!forgotPasswordOtp) {
      setForgotPasswordError("Please enter the OTP.");
      setForgotPasswordLoading(false);
      return;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL;

      const response = await fetch(`${API_BASE_URL}/PatientPortal/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: forgotPasswordEmail,
          otp: forgotPasswordOtp,
        }),
      });

      const raw = await response.text();
      let res = {};
      try {
        res = raw ? JSON.parse(raw) : {};
      } catch {
        res = { message: raw };
      }

      if (!response.ok) {
        throw new Error(res.message || "Invalid OTP. Please try again.");
      }

      setForgotPasswordSuccessMessage(res.message || "OTP verified.");
      setForgotPasswordCurrentStep(FORGOT_PASSWORD_STEPS.RESET_PASSWORD);
    } catch (err) {
      setForgotPasswordError(err.message || "Failed to verify OTP.");
    } finally {
      setForgotPasswordLoading(false);
    }
  };
  
  // 3) Reset Password: POST /PatientPortal/reset-password body: { email, newPassword, confirmPassword }
 const handleResetPassword = async () => {
  setForgotPasswordLoading(true);
  setForgotPasswordError("");
  setForgotPasswordSuccessMessage("");

  if (forgotPasswordNewPassword.length < 6) {
    setForgotPasswordError("New password must be at least 6 characters long.");
    setForgotPasswordLoading(false);
    return;
  }
  if (forgotPasswordNewPassword !== forgotPasswordConfirmPassword) {
    setForgotPasswordError("Passwords do not match.");
    setForgotPasswordLoading(false);
    return;
  }

  try {
    const API_BASE_URL = process.env.REACT_APP_API_URL;

    // MD5 hash both password fields before sending
    const hashedNewPassword = md5(forgotPasswordNewPassword);
    const hashedConfirmPassword = md5(forgotPasswordConfirmPassword);

    const payload = {
      email: forgotPasswordEmail,
      newPassword: hashedNewPassword,
      confirmPassword: hashedConfirmPassword,
    };

    const response = await fetch(`${API_BASE_URL}/PatientPortal/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const raw = await response.text();
    let res = {};
    try {
      res = raw ? JSON.parse(raw) : {};
    } catch {
      res = { message: raw };
    }

    if (!response.ok) {
      throw new Error(res.message || "Failed to reset password. Please try again.");
    }

    setForgotPasswordSuccessMessage(res.message || "Password reset successfully!");
    setForgotPasswordCurrentStep(FORGOT_PASSWORD_STEPS.SUCCESS);
  } catch (err) {
    setForgotPasswordError(err.message || "Failed to reset password.");
  } finally {
    setForgotPasswordLoading(false);
  }
};

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        bgcolor: "#ffffff",
        zIndex: 9999,
        overflow: "hidden",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      {/* =============== 📱 MOBILE TOP CIRCLE (STATIC) WITH NEURONS =============== */}
      <Box
        sx={{
          display: { xs: "block", md: "none" },
          position: "absolute",
          top: "-35%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "140%",
          height: "70%",
          borderRadius: "50%",
          background: "radial-gradient(circle at 50% 80%, #42a5f5 0%, #0055b3 85%)",
          zIndex: 4,
          boxShadow: "0 10px 40px rgba(0,85,179,0.3)",
          overflow: "hidden",
        }}
      >
        <MiniNeuralNetwork />

        {/* Mobile Welcome Text */}
        <Box
          sx={{
            position: "absolute",
            bottom: "6%",
            left: "50%",
            transform: "translateX(-50%)",
            textAlign: "center",
            zIndex: 4,
            width: "80%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1.5,
              mt: 6,
            }}
          >
            <Box
              component={motion.div}
              animate={{ rotateY: [0, 180, 360] }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <img
                src="/images/newlogo.PNG"
                alt="logo"
                style={{ height: 100, width: "auto", display: "block" }}
              />
            </Box>
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "white",
              mb: 1,
              mt: 2,
              display: { xs: "block", md: "none" },
            }}
          >
            Sign In
          </Typography>
        </Box>
      </Box>

      {/* =============== LEFT SIDE (Hidden on Mobile) =============== */}
      <Box
        component={motion.div}
        initial={{ x: -40, opacity: 0 }}
        animate={{
          x: playSuccessAnim ? -400 : 0,
          opacity: playSuccessAnim ? 0 : 1,
        }}
        transition={{ duration: 0.8, ease: "easeInOut" }}
        sx={{
          display: { xs: "none", md: "flex" },
          flex: { md: "0 0 50%" },
          position: "relative",
          bgcolor: "transparent",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          pl: { md: 12 },
          pr: { md: 6 },
          overflow: "hidden",
        }}
      >
        {/* 1. MAIN BG CIRCLE */}
        <Box
          component={motion.div}
          initial={{ scale: 0.95, x: -30 }}
          animate={{
            scale: playSuccessAnim ? 1.2 : [1, 1.03, 1],
            x: playSuccessAnim ? -400 : [0, -10, 0],
          }}
          transition={{
            duration: playSuccessAnim ? 0.8 : 4,
            ease: "easeInOut",
            repeat: playSuccessAnim ? 0 : Infinity,
            repeatType: "mirror",
          }}
          sx={{
            position: "absolute",
            top: "-35%",
            left: "-28%",
            width: "120%",
            height: "120%",
            borderRadius: "50%",
            background: "radial-gradient(circle at 20% 10%, #42a5f5 0, #0055b3 85%)",
            zIndex: 0,
          }}
        />

        {/* 2. BOTTOM-LEFT CIRCLE (with neurons) */}
        <Box
          component={motion.div}
          initial={{ y: 40, opacity: 0.8 }}
          animate={{
            y: playSuccessAnim ? 200 : [0, 10, 0],
            opacity: playSuccessAnim ? 0 : 1,
          }}
          transition={{
            duration: playSuccessAnim ? 0.8 : 3,
            ease: "easeInOut",
            repeat: playSuccessAnim ? 0 : Infinity,
            repeatType: "mirror",
          }}
          sx={{
            position: "absolute",
            bottom: "-5%",
            left: "-13%",
            width: "45%",
            height: "45%",
            borderRadius: "50%",
            background: "radial-gradient(circle at 20% 10%, #42a5f5 0, #0055b3 85%)",
            zIndex: 1,
            boxShadow: "-10px 10px 30px rgba(0,0,0,0.2)",
            overflow: "hidden",
          }}
        >
          <MiniNeuralNetwork />
        </Box>

        {/* 3. MIDDLE CIRCLE (with neurons) */}
        <Box
          component={motion.div}
          initial={{ y: 30, opacity: 1 }}
          animate={{
            y: playSuccessAnim ? -200 : [0, -8, 0],
            opacity: playSuccessAnim ? 0.6 : 1,
          }}
          transition={{
            duration: playSuccessAnim ? 0.8 : 3.5,
            ease: "easeInOut",
            repeat: playSuccessAnim ? 0 : Infinity,
            repeatType: "mirror",
          }}
          sx={{
            position: "absolute",
            top: "60%",
            right: "20%",
            transform: "translateY(-20%)",
            width: "35%",
            height: "35%",
            borderRadius: "50%",
            background: "radial-gradient(circle at 20% 10%, #42a5f5 0, #0055b3 85%)",
            zIndex: 2,
            boxShadow: "-10px 10px 30px rgba(0,0,0,0.2)",
            display: { xs: "none", md: "block" },
            overflow: "hidden",
          }}
        >
          <MiniNeuralNetwork />
        </Box>

        {/* LEFT TEXT */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, x: -20 }}
          animate={{
            opacity: playSuccessAnim ? 0 : 1,
            x: playSuccessAnim ? -100 : 0,
          }}
          transition={{ duration: 0.8, delay: 0.2 }}
          sx={{ position: "relative", zIndex: 3, maxWidth: "480px", mb: 20 }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              mb: 1,
              letterSpacing: "1px",
              color: "#ffffff",
              fontSize: "3.5rem",
              textShadow: "0px 0px 20px rgba(0,0,0,0.5)",
            }}
          >
            WELCOME
          </Typography>
          <Typography
            sx={{
              mb: 3,
              fontWeight: 600,
              letterSpacing: "3px",
              fontSize: "14px",
              color: "#E3F2FD",
              textTransform: "uppercase",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <span
              style={{
                width: "10px",
                height: "10px",
                background: "#00E5FF",
                borderRadius: "50%",
                display: "inline-block",
                boxShadow: "0 0 10px #00E5FF",
              }}
            ></span>
            NeuroEZ Patient Portal
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255,255,255,0.9)",
              lineHeight: 1.7,
              fontSize: "16px",
              fontWeight: 300,
            }}
          >
            Access your medical records, communicate with your doctors, and manage
            your health seamlessly with our secure neural network technology.
          </Typography>
        </Box>
      </Box>

      {/* =============== RIGHT SIDE (FORM) =============== */}
      <Box
        component={motion.div}
        initial={{ x: 40, opacity: 0 }}
        animate={{
          x: playSuccessAnim ? 400 : 0,
          opacity: playSuccessAnim ? 0 : 1,
        }}
        transition={{ duration: 0.8, ease: "easeInOut" }}
        onAnimationComplete={handleAnimationComplete}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: { xs: 2, md: 8 },
          position: "relative",
          zIndex: 10,
        }}
      >
        {/* bottom-right circle */}
        <Box
          component={motion.div}
          animate={{
            scale: playSuccessAnim ? 1.3 : [1, 1.05, 1],
            x: playSuccessAnim ? 250 : 0,
            y: playSuccessAnim ? 150 : [0, -10, 0],
          }}
          transition={{
            duration: playSuccessAnim ? 0.8 : 4,
            ease: "easeInOut",
            repeat: playSuccessAnim ? 0 : Infinity,
            repeatType: "mirror",
          }}
          sx={{
            display: { xs: "none", sm: "block" },
            position: "absolute",
            bottom: "-80px",
            right: "-80px",
            width: "45%",
            height: "45%",
            borderRadius: "50%",
            background: "radial-gradient(circle at 20% 10%, #42a5f5 0, #0055b3 85%)",
            zIndex: 0,
            boxShadow: "-10px 10px 30px rgba(0,0,0,0.2)",
            opacity: 0.9,
          }}
        />
        <MiniNeuralNetwork />

        {/* FORM CONTAINER */}
        <Box
          component={motion.div}
          initial={{ y: 40, opacity: 0 }}
          animate={{
            y: playSuccessAnim ? -250 : 0,
            opacity: playSuccessAnim ? 0 : 1,
          }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          sx={{
            width: "100%",
            maxWidth: "380px",
            position: "relative",
            zIndex: 2,
            mb: 5,
            mr: { xs: "auto", md: "60%", lg: "70%" },
            ml: { xs: "auto", md: 8, lg: 10 },
            mt: { xs: "-5%", md: "-10%", lg: "-20%" },
          }}
        >
          {/* LOGO (desktop) */}
          <Box
            sx={{
              mt: { xs: 4, md: 20 },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: { xs: 0, md: 10, lg: 4 },
            }}
          >
            <Box
              component={motion.div}
              animate={{ rotateY: [0, 180, 360] }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Box
                component="img"
                src="/images/newlogo1.PNG"
                alt="logo"
                sx={{
                  height: 100,
                  width: "auto",
                  display: { xs: "none", md: "block" },
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              textAlign: "center",
              zIndex: 15,
              mt: { xs: 25, md: 0 },
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#111827",
                mb: 1,
                display: { xs: "none", md: "block" },
              }}
            >
              Sign In
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: { xs: "#333", md: "#333" },
                fontSize: { xs: "16px", md: "20px" },
                mb: { xs: 4, md: 3 },
              }}
            >
              Enter Your{" "}
              <span
                style={{
                  color: "#1565C0",
                  backgroundColor: "#E3F2FD",
                  padding: "0 4px",
                }}
              >
                Credentials
              </span>{" "}
              To Access Your Account
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: "8px" }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            {/* EMAIL FIELD (with validation) */}
            <Box sx={{ mb: { xs: 4, md: 1, lg: 3 } }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#555" }}>
                Email Address
              </Typography>
              <TextField
                fullWidth
                name="emailAddress"
                placeholder="Enter Your Email"
                value={formData.emailAddress}
                onChange={handleChange}
                variant="outlined"
                error={!!emailError}
                helperText={emailError}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#E3F2FD",
                    borderRadius: "8px",
                    height: { xs: "auto", md: "auto", lg: "55px" },
                    "& fieldset": { borderColor: "#42A5F5" },
                    "&:hover fieldset": { borderColor: "#1E88E5" },
                    "&.Mui-focused fieldset": {
                      borderColor: "#42A5F5",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    py: 1,
                    fontSize: "14px",
                    color: "#333",
                    px: 1,
                  },
                  "& .MuiFormHelperText-root": {
                    mt: 0.5,
                    fontSize: "11px",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: "#9CA3AF" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* PASSWORD FIELD */}
            <Box sx={{ mb: { xs: 8, md: 1, lg: 2 } }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#555",  }}>
                Password
              </Typography>
              <TextField
                fullWidth
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#E3F2FD",
                    borderRadius: "8px",
                    height: { xs: "auto", md: "auto", lg: "55px" },
                    "& fieldset": { borderColor: "#42A5F5" },
                    "&:hover fieldset": { borderColor: "#1E88E5" },
                    "&.Mui-focused fieldset": {
                      borderColor: "#42A5F5",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    py: 1,
                    fontSize: "14px",
                    color: "#333",
                    px: 1,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <HttpsIcon sx={{ color: "#9CA3AF" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        onClick={() => setShowPassword((p) => !p)}
                        sx={{
                          fontSize: "12px",
                          fontWeight: 700,
                          color: "#6B7280",
                          cursor: "pointer",
                          textTransform: "uppercase",
                          userSelect: "none",
                        }}
                      >
                        {showPassword ? "Hide" : "Show"}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ mt: 1, textAlign: "right" }}>
              <button
                type="button"
                style={{
                  background: "none",
                  border: "none",
                  color: "#007bff",
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontSize: "0.9rem",
                
                }}
                onClick={() => setShowForgotPasswordModal(true)}
              >
                Forgot Password?
              </button>
            </Box>

            {/* Forgot Password Modal */}
            <Modal
              open={showForgotPasswordModal}
              onClose={handleForgotPasswordClose}
              aria-labelledby="forgot-password-modal-title"
              aria-describedby="forgot-password-modal-description"
              sx={{ zIndex: (theme) => theme.zIndex.modal + 10000 }}
            >
              <Box sx={forgotPasswordModalStyle}>
                <Typography id="forgot-password-modal-title" variant="h6" component="h2" mb={2}>
                  {forgotPasswordCurrentStep === FORGOT_PASSWORD_STEPS.EMAIL_INPUT && "Forgot Password"}
                  {forgotPasswordCurrentStep === FORGOT_PASSWORD_STEPS.OTP_INPUT && "Verify OTP"}
                  {forgotPasswordCurrentStep === FORGOT_PASSWORD_STEPS.RESET_PASSWORD && "Reset Password"}
                  {forgotPasswordCurrentStep === FORGOT_PASSWORD_STEPS.SUCCESS && "Password Reset Successful!"}
                </Typography>
                <Box id="forgot-password-modal-description">
                  {forgotPasswordLoading && (
                    <Box display="flex" justifyContent="center" my={2}>
                      <CircularProgress />
                    </Box>
                  )}

                  {forgotPasswordError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {forgotPasswordError}
                    </Alert>
                  )}
                  {forgotPasswordSuccessMessage && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {forgotPasswordSuccessMessage}
                    </Alert>
                  )}

                  {forgotPasswordCurrentStep === FORGOT_PASSWORD_STEPS.EMAIL_INPUT && (
                    <>
                      <TextField
                        autoFocus
                        margin="dense"
                        id="forgot-password-email"
                        label="Email Address"
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={forgotPasswordEmail}
                        onChange={(e) => setForgotPasswordEmail(e.target.value)}
                        disabled={forgotPasswordLoading}
                        helperText="Enter your registered email to receive an OTP."
                      />
                      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
                        <Button onClick={handleForgotPasswordClose} disabled={forgotPasswordLoading} color="secondary">
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSendOtp}
                          disabled={forgotPasswordLoading || !forgotPasswordEmail}
                          variant="contained"
                          sx={{ ml: 1 }}
                        >
                          {forgotPasswordLoading ? <CircularProgress size={24} /> : "Send OTP"}
                        </Button>
                      </Box>
                    </>
                  )}

                  {forgotPasswordCurrentStep === FORGOT_PASSWORD_STEPS.OTP_INPUT && (
                    <>
                      <TextField
                        autoFocus
                        margin="dense"
                        id="forgot-password-otp"
                        label="OTP"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={forgotPasswordOtp}
                        onChange={(e) => setForgotPasswordOtp(e.target.value)}
                        disabled={forgotPasswordLoading}
                        helperText={`An OTP has been sent to ${forgotPasswordEmail}.`}
                      />
                      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
                        <Button onClick={handleForgotPasswordClose} disabled={forgotPasswordLoading} color="secondary">
                          Cancel
                        </Button>
                        <Button
                          onClick={handleVerifyOtp}
                          disabled={forgotPasswordLoading || !forgotPasswordOtp}
                          variant="contained"
                          sx={{ ml: 1 }}
                        >
                          {forgotPasswordLoading ? <CircularProgress size={24} /> : "Verify OTP"}
                        </Button>
                      </Box>
                    </>
                  )}

                  {forgotPasswordCurrentStep === FORGOT_PASSWORD_STEPS.RESET_PASSWORD && (
                    <Box>
                      <TextField
                        autoFocus
                        margin="dense"
                        id="forgot-password-new-password"
                        label="New Password"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={forgotPasswordNewPassword}
                        onChange={(e) => setForgotPasswordNewPassword(e.target.value)}
                        disabled={forgotPasswordLoading}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        margin="dense"
                        id="forgot-password-confirm-password"
                        label="Confirm New Password"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={forgotPasswordConfirmPassword}
                        onChange={(e) => setForgotPasswordConfirmPassword(e.target.value)}
                        disabled={forgotPasswordLoading}
                      />
                      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
                        <Button onClick={handleForgotPasswordClose} disabled={forgotPasswordLoading} color="secondary">
                          Cancel
                        </Button>
                        <Button
                          onClick={handleResetPassword}
                          disabled={
                            forgotPasswordLoading || !forgotPasswordNewPassword || !forgotPasswordConfirmPassword
                          }
                          variant="contained"
                          sx={{ ml: 1 }}
                        >
                          {forgotPasswordLoading ? <CircularProgress size={24} /> : "Reset Password"}
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {forgotPasswordCurrentStep === FORGOT_PASSWORD_STEPS.SUCCESS && (
                    <Box textAlign="center">
                      <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
                        Your password has been successfully reset. You can now log in with your new password.
                      </Typography>
                      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
                        <Button onClick={handleForgotPasswordClose} variant="contained">
                          Close
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Modal>

            <Button
              type="submit"
              fullWidth
              disabled={loading}
              sx={{
                bgcolor: "#0288d1",
                color: "#ffffff",
                py: { xs: 1.2, md: 1.2, lg: 1.8 },
                mt: { xs: 0, md: 1, lg: 3 },
                borderRadius: "30px",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 600,
                mb: 4,
                boxShadow: "0 4px 10px rgba(2, 136, 209, 0.4)",
                "&:hover": { bgcolor: "#01579b" },
                
              }}
            >
              {loading ? "Please Wait" : "Sign In"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SignInForm;